import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, auth } from '../../firebase/config';
import { 
    collection, 
    doc 
} from 'firebase/firestore';

// Utilidades centralizadas del sistema (Pilares del Canvas)
import { saveToFirestore } from '../../firebase/cleanPayload';
import { useSegurosData } from '../../context/SegurosDataContext';
import { EstadoEndosoArt } from '../../data/listasDesplegables';

// Importamos el ecosistema centralizado de Seguros
import { parseCurrency, parsePercent, formatCurrencyForInput } from '../../utils/segurosUtils';
import { CurrencyInput, PercentInput } from '../../utils/FormInputs';

// --- HELPERS EXCLUSIVOS DEL FLUJO DE AUMENTO ---
const formatMMYYYY = (value) => {
    let clean = value.replace(/\D/g, '').slice(0, 6);
    if (clean.length > 2) {
        return `${clean.slice(0, 2)}/${clean.slice(2)}`;
    }
    return clean;
};

const generarNumeroEndoso = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `END-${result}`; 
};

/**
 * FormularioAumentosArt - Gestión y Registro de Endosos por Incremento de Tarifa (ART)
 */
const FormularioAumentosArt = ({ entityData: clientData, contractData, onSuccess, onCancel, initialData = null }) => {
    
    // --- CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    const defaultFormData = {
        numeroEndoso: '',
        fechaCarga: new Date().toISOString().split('T')[0],
        mesAumento: '',
        periodoArca: '',
        contratoId: '',
        nContrato: '',
        compania: '',
        companiaId: '',
        ciiu: '',
        capitas: '',
        masaSalarial: '',
        alicuotaActual: '',
        alicuotaAumentada: '',
        porcentajeAumento: 0,
        vendedor: '',
        canal: '', 
        canalNombreId: '',
        canalNombre: '',
        subcanalNombreId: '',
        subcanalNombre: '',
        estado: 'Pendiente', 
        responsableEndoso: '', // ◄ NUEVA PROPIEDAD: Responsable del endoso comercial
        observaciones: ''
    };

    const [formData, setFormData] = useState(defaultFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const isHydrated = useRef(false);

    // --- COLABORADORES ORDENADOS ALFABÉTICAMENTE ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    // 1. SINCRONIZACIÓN DE DATOS (Hidratación controlada con nuevos campos)
    useEffect(() => {
        if (isHydrated.current) return;

        if (initialData) {
            setFormData({
                ...defaultFormData,
                ...initialData,
                ciiu: initialData.ciiu || '',
                canal: initialData.canal || initialData.tipoCanal || '',
                canalNombreId: initialData.canalNombreId || '',
                subcanalNombreId: initialData.subcanalNombreId || '',
                companiaId: initialData.companiaId || '',
                masaSalarial: formatCurrencyForInput(initialData.masaSalarial),
                alicuotaActual: initialData.alicuotaActual ? String(initialData.alicuotaActual).replace('.', ',') : '',
                alicuotaAumentada: initialData.alicuotaAumentada ? String(initialData.alicuotaAumentada).replace('.', ',') : '',
                estado: initialData.estado || 'Pendiente',
                responsableEndoso: initialData.responsableEndoso || '' // ◄ ADAPTADOR: Hidratación del responsable
            });
            isHydrated.current = true;
        } else if (contractData) {
            setFormData(prev => ({
                ...prev,
                numeroEndoso: generarNumeroEndoso(),
                contratoId: contractData.id,
                nContrato: contractData.nContrato || '',
                compania: contractData.compania || '',
                companiaId: contractData.companiaId || '',
                ciiu: contractData.ciiu || '', 
                vendedor: contractData.vendedor || '',
                canal: contractData.canal || contractData.tipoCanal || '', 
                canalNombreId: contractData.canalNombreId || '',
                canalNombre: contractData.canalNombre || '',
                subcanalNombreId: contractData.subcanalNombreId || '',
                subcanalNombre: contractData.subcanalNombre || '',
                estado: 'Pendiente',
                responsableEndoso: '' // Inicialización en limpio
            }));
            isHydrated.current = true;
        }
    }, [initialData, contractData]);

    // 2. CÁLCULO DE IMPACTO (Segurizado usando helpers centralizados)
    useEffect(() => {
        const actual = parsePercent(formData.alicuotaActual);
        const aumentada = parsePercent(formData.alicuotaAumentada);
        if (actual > 0) {
            const diff = ((aumentada - actual) / actual) * 100;
            setFormData(prev => ({ ...prev, porcentajeAumento: diff.toFixed(2) }));
        } else {
            setFormData(prev => ({ ...prev, porcentajeAumento: 0 }));
        }
    }, [formData.alicuotaActual, formData.alicuotaAumentada]);

    const getFullClientName = () => {
        if (clientData) {
            if (clientData.persona === 'JURIDICA') return (clientData.nombre || '').trim().toUpperCase();
            const n = clientData.nombre || '';
            const a = clientData.apellido || '';
            return `${n} ${a}`.trim().toUpperCase();
        }
        return (initialData?.clienteNombre || 'Cargando...').toUpperCase();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'mesAumento' || name === 'periodoArca') {
            setFormData(prev => ({ ...prev, [name]: formatMMYYYY(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleNumericOnly = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value.replace(/\D/g, '') }));
    };

    const handleCurrencyChange = (e) => {
        let val = e.target.value.replace(/\D/g, ''); 
        if (val) {
            const num = Number(val) / 100;
            setFormData(prev => ({ ...prev, masaSalarial: formatCurrencyForInput(num) }));
        } else {
            setFormData(prev => ({ ...prev, masaSalarial: '' }));
        }
    };

    const handlePercentChange = (e) => {
        const { name, value } = e.target;
        let val = value.replace('.', ',');
        if (/^[0-9,]*$/.test(val)) {
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        
        const clientId = clientData?.id || initialData?.clienteId;
        const contractId = contractData?.id || formData.contratoId;

        if (!clientId || !contractId) {
            console.error("Faltan datos de vinculación crítica.");
            return;
        }

        setIsSubmitting(true);

        try {
            const isJuridica = clientData?.persona === 'JURIDICA';
            
            // ◄ SOLUCIÓN: Limpiamos los campos de auditoría manuales del payload.
            // saveToFirestore estampará de forma totalmente centralizada y transparente la auditoría (Hito 3)
            const rawPayload = {
                ...formData,
                masaSalarial: parseCurrency(formData.masaSalarial),
                capitas: parseInt(formData.capitas) || 0,
                ciiu: String(formData.ciiu || '').trim(),
                alicuotaActual: parsePercent(formData.alicuotaActual),
                alicuotaAumentada: parsePercent(formData.alicuotaAumentada),
                porcentajeAumento: parseFloat(formData.porcentajeAumento),
                observaciones: String(formData.observaciones || '').trim(),
                
                // --- DATOS NOMENCLATURA CLIENTE ---
                clienteId: clientId,
                clienteNombre: (clientData?.nombre || initialData?.clienteNombre || '').trim().toUpperCase(),
                clienteApellido: isJuridica ? '' : (clientData?.apellido || initialData?.clienteApellido || '').trim().toUpperCase(),
                clienteCuit: clientData?.cuit || initialData?.clienteCuit || '',
                clienteDni: clientData?.dni || initialData?.clienteDni || ''
            };

            const collectionPath = `public/data/clientes/${clientId}/contratos/${contractId}/aumentos_art`;
            
            // ◄ REFACTORIZACIÓN SENIOR: Persistimos mediante saveToFirestore centralizado
            await saveToFirestore(collectionPath, rawPayload, initialData?.id || null);
            if (onSuccess) {
                onSuccess(initialData?.id ? 'Aumento actualizado correctamente.' : 'Aumento guardado correctamente.');
            }
        } catch (error) {
            console.error("Error al procesar aumento:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-fade-in max-w-5xl mx-auto text-left">
            
            <div className="bg-orange-50 px-8 py-6 border-b border-orange-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-xl font-bold text-orange-900 flex items-center gap-2 uppercase tracking-tighter">
                        {initialData ? 'Editar Aumento' : 'Cargar Aumento'} ART
                        <span className="text-[10px] font-black text-orange-700 bg-orange-100 px-2 py-0.5 rounded border border-orange-200 uppercase tracking-widest text-left">
                            Aumento
                        </span>
                    </h2>
                    <div className="flex items-center gap-2 mt-1 text-orange-800 font-bold uppercase text-left">
                        <span className="text-lg tracking-tight text-left">{getFullClientName()}</span>
                        <span className="text-orange-400">|</span>
                        <span className="font-mono text-sm tracking-tighter text-left">
                            {clientData?.cuit || clientData?.dni || initialData?.clienteCuit || initialData?.clienteDni || '-'}
                        </span>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8 text-left">
                
                {/* 1. SECCIÓN DE METADATOS DE CONTROL */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-gray-50 p-4 rounded-lg border border-gray-200 text-left">
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">N° Solicitud Endoso</label>
                        <input type="text" value={formData.numeroEndoso} readOnly className="input-round bg-white font-mono text-center font-bold text-orange-600 border-orange-200 cursor-default shadow-none focus:border-slate-200 focus:ring-0" />
                    </div>

                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Vincular a Contrato</label>
                        <input type="text" value={formData.nContrato ? `#${formData.nContrato}` : ''} readOnly className="input-round bg-white font-mono text-center font-bold text-blue-700 border-blue-100 cursor-default shadow-none focus:border-slate-200 focus:ring-0" />
                    </div>
                    
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Compañía ART</label>
                        <input type="text" value={formData.compania} readOnly className="input-round bg-gray-100 text-gray-600 font-bold uppercase cursor-default focus:border-slate-200 focus:ring-0" />
                    </div>

                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Fecha de Carga</label>
                        <input type="date" name="fechaCarga" value={formData.fechaCarga} onChange={handleChange} className="input-round border-slate-200 text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" required />
                    </div>
                </div>

                {/* 2. SECCIÓN DE PLAZOS, ESTADO Y RESPONSABLE */}
                {/* ◄ MEJORA INTERACTIVA: Ampliamos el grid de 3 a 4 columnas para posicionar prolijamente el nuevo campo */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-left">
                    <div className="text-left">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Mes de Aumento</label>
                        <input type="text" name="mesAumento" value={formData.mesAumento} onChange={handleChange} placeholder="MM/YYYY" className="input-round border-slate-200 font-mono text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" required />
                    </div>
                    <div className="text-left">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Periodo ARCA</label>
                        <input type="text" name="periodoArca" value={formData.periodoArca} onChange={handleChange} placeholder="MM/YYYY" className="input-round border-slate-200 font-mono text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" required />
                    </div>
                    <div className="text-left">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Estado del Endoso</label>
                        <select name="estado" value={formData.estado} onChange={handleChange} className="input-round border-slate-200 cursor-pointer font-bold text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" required>
                            {EstadoEndosoArt.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                    </div>
                    {/* ◄ NUEVO CAMPO: Responsable del Endoso (responsableEndoso) conectado dinámicamente con colaboradores */}
                    <div className="text-left animate-fade-in">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Responsable *</label>
                        <select 
                            name="responsableEndoso" 
                            value={formData.responsableEndoso} 
                            onChange={handleChange} 
                            className="input-round border-slate-200 cursor-pointer font-bold text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" 
                            required
                        >
                            <option value="">Seleccione...</option>
                            {sortedColaboradores.map(c => (
                                <option key={c.id} value={c.nombre}>{c.nombre}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* 3. FIELDSET DE INCREMENTOS (Utilizando componentes atómicos del Canvas) */}
                <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/5 text-left italic">
                    <legend className="px-2 text-lg font-bold text-orange-700 bg-white absolute -top-3 left-4 italic text-left">Datos del Incremento</legend>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-5 pt-2 text-left items-end">
                        <div className="text-left">
                            <label className="label-form text-sm font-bold text-gray-600 mb-1 text-left">CIIU</label>
                            <input type="text" name="ciiu" value={formData.ciiu} onChange={handleNumericOnly} className="input-round border-slate-200 font-mono text-center text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" placeholder="000000" />
                        </div>
                        <div className="text-left">
                            <label className="label-form text-sm font-bold text-gray-600 mb-1 text-left">Cápitas</label>
                            <input type="number" name="capitas" value={formData.capitas} onChange={handleChange} className="input-round border-slate-200 font-mono text-center text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" placeholder="0" />
                        </div>
                        
                        {/* Masa Salarial con Input de Moneda Atómico */}
                        <div className="lg:col-span-2 text-left">
                            <CurrencyInput 
                                label="Masa Salarial ($)"
                                value={formData.masaSalarial}
                                onChange={handleCurrencyChange}
                                focusColor="focus-within:border-orange-500 focus-within:ring-orange-100"
                            />
                        </div>

                        {/* Alícuota Actual con Input de Porcentaje Atómico */}
                        <div className="text-left">
                            <PercentInput 
                                label="Alic. Actual (%)"
                                name="alicuotaActual"
                                value={formData.alicuotaActual}
                                onChange={handlePercentChange}
                                focusColor="focus-within:border-orange-500 focus-within:ring-orange-100"
                            />
                        </div>

                        {/* Alícuota Aumentada con Input de Porcentaje Atómico de alta fidelidad */}
                        <div className="lg:col-span-1 text-left">
                            <PercentInput 
                                label="Aumentada (%)"
                                name="alicuotaAumentada"
                                value={formData.alicuotaAumentada}
                                onChange={handlePercentChange}
                                focusColor="focus-within:border-orange-500 focus-within:ring-orange-100"
                                textColor="text-orange-800 font-black"
                            />
                        </div>
                    </div>

                    {/* Métrica de impacto visual */}
                    <div className="mt-8 flex justify-center text-left animate-fade-in">
                        <div className="bg-white border border-dashed border-orange-200 p-4 rounded-xl text-center min-w-[240px] shadow-sm text-left">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 leading-none text-left">Impacto del Aumento</p>
                            <p className={`text-3xl font-black text-left ${parseFloat(formData.porcentajeAumento) > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
                                {formData.porcentajeAumento}%
                            </p>
                        </div>
                    </div>
                </fieldset>

                {/* 4. OBSERVACIONES */}
                <div className="text-left">
                    <label className="block text-sm font-bold text-gray-600 mb-2 text-left">Observaciones</label>
                    <textarea name="observaciones" rows="3" value={formData.observaciones} onChange={handleChange} className="input-round border-slate-200 h-auto min-h-[100px] resize-none p-4 text-sm text-left focus:border-orange-500 focus:ring-1 focus:ring-orange-100" placeholder="Detalles sobre el motivo del aumento, gestiones o notas comerciales..."></textarea>
                </div>

                {/* 5. ACCIONES DE GUARDADO */}
                <div className="flex justify-end gap-4 pt-6 border-t border-gray-100 text-left">
                    <button type="button" onClick={onCancel} className="px-6 py-3 rounded-lg border border-gray-300 font-bold text-gray-600 hover:bg-slate-50 transition shadow-sm uppercase text-xs tracking-widest text-left">Cancelar</button>
                    <button type="submit" disabled={isSubmitting} className="px-10 py-3 rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700 shadow-lg disabled:opacity-50 transform active:scale-95 transition-all uppercase text-xs tracking-widest text-left">
                        {isSubmitting ? 'Guardando...' : (initialData ? 'Actualizar Registro' : 'Confirmar Endoso')}
                    </button>
                </div>
            </form>
            <style>{`
                .input-round { width: 100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e5e7eb; outline: none; transition: all 0.2s; font-size: 0.95rem; background-color: #fff; height: 46px; }
                .input-round:focus { border-color: #f97316; box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1); }
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default FormularioAumentosArt;